import { useState } from "react";
import BulbIcon from "../../../../../assets/svg/bulb-icon";
import WebsiteIcon from "../../../../../assets/svg/website-icon";
import Button from "../../../../../components/Button/Button";
import Input from "../../../../../components/Input/Input";
import { useScrapeUrlMutation } from "../../../../../redux/services/playgroundApi";
import { PLAYGROUND_CONFIG } from "../../../utils/constants";
import { customToast } from "../../../../../utils/custom-toast/custom-toast";
import { trackEvents } from "../../../../../utils/amplitude/amplitude";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";

const AddWebsite = ({
  onClose,
  setScrollToWebsite,
}: {
  onClose: () => void;
  setScrollToWebsite?: (scrollToWebsite: string | null) => void;
}) => {
  const [scrapeUrl, { isLoading }] = useScrapeUrlMutation();
  const orgId = useSelector((state: RootState) => state?.auth?.client?.client_code) || "unknown";
  const userId = useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";
  const [url, setUrl] = useState("");
  const [websiteExistsError, setWebsiteExistsError] = useState(false);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrl(e.target.value);
  };

  const handleUrlSubmit = async (url: string) => {
    if (!url) return;
    try {
      const response = await scrapeUrl({ seed_url: url })?.unwrap();
      if (
        response?.error &&
        response?.error?.includes(
          PLAYGROUND_CONFIG.WEB_SYNC_STATUS.WEBSITE_EXISTS_ERROR
        )
      ) {
        setWebsiteExistsError(true);
        return;
      }
      let domainName = "unknown";
      try { domainName = new URL(url).hostname; } catch { domainName = "unknown"; }
      trackEvents("url_added_to_kb", { url, domain_name: domainName, added_by: userId, org_id: orgId });
      customToast(
        PLAYGROUND_CONFIG.MESSAGES.MODAL.SYNC_WEBSITE.SUCCESS,
        "success",
        undefined,
        undefined,
        "bottom-left"
      );
    } catch (error) {
      console.error(PLAYGROUND_CONFIG.MESSAGES.MODAL.SYNC_WEBSITE.ERROR, error);
      customToast(
        PLAYGROUND_CONFIG.MESSAGES.MODAL.SYNC_WEBSITE.ERROR,
        "error",
        undefined,
        undefined,
        "bottom-left"
      );
    } finally {
      onClose();
      setScrollToWebsite?.("website");
    }
  };

  const allowedDomains = ["http:", "https:"];

  const validateUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return allowedDomains?.includes(urlObj?.protocol);
    } catch {
      return false;
    }
  };
  const isValidUrl = url.trim() && validateUrl(url);

  return (
    <div className="flex flex-col gap-2.5 h-full">
      <WebsiteIcon className="w-11.5 h-11.5 text-verba-blue dark:text-verba-blue-dk" />
      <h2 className="text-2xl font-medium text-verba-indigo dark:text-verba-indigo-dk">
        Sync Content from any Website
      </h2>
      <p className="text-base text-verba-indigo dark:text-verba-indigo-dk">
        Verbaflo scrapes your website to train its AI, enabling a deeper
        understanding of your business for more accurate and personalized
        communications.
      </p>
      <div className="mt-[38px]">
        <Input
          placeholder="https://www.verbaflo.ai"
          type="url"
          onChange={handleUrlChange}
          value={url}
          secondaryContainerClassName="w-full p-2 border border-border-gray dark:border-border-gray-dk rounded-lg w-[324px]"
          label="Enter Website URL"
          labelClassName="text-verba-blue dark:text-verba-blue-dk font-medium text-sm font-medium"
        />
      </div>
      {websiteExistsError && (
        <p className="text-sm text-verba-red-alert dark:text-verba-red-alert-dk">
          Website already exists
        </p>
      )}

      <div className="flex flex-col gap-2.5 mt-[38px]">
        <BulbIcon className="w-11.5 h-11.5 text-verba-blue dark:text-verba-blue-dk" />
        <p className="text-base text-data dark:text-data-dk text-left">
          <span className="font-semibold">Pro Tip:</span> For best results,
          enter a publicly accessible root domain
          (e.g.,https://myhelpcenter.com). Verbaflo will auto-sync all linked
          pages, Avoid deep links for full coverage.
        </p>
      </div>
      <div className="flex justify-end gap-2 mt-auto">
        <Button
          innerText="Cancel"
          onClick={onClose}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
          innerTextClass="text-md text-heading-text dark:text-heading-text-dk font-medium"
        />
        <Button
          innerText="Continue"
          onClick={() => handleUrlSubmit(url)}
          isLoading={isLoading}
          disabled={!isValidUrl}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed hover:bg-verba-blue dark:hover:bg-verba-blue-dk text-pure-white dark:text-pure-white-dk hover:text-pure-white-dk dark:hover:text-pure-white"
          innerTextClass="text-md font-medium whitespace-nowrap"
        />
      </div>
    </div>
  );
};

export default AddWebsite;
